import type { GameRules } from "@obean/game-contract";
import { createRng } from "./rng.js";
import { Ring, percentile } from "./window.js";

/** How the local player is drawn: a few continuous values the game reads from its state. */
export interface LocalView<S> {
  /** e.g. [x, y, heading, turret] for the local player's tank. Same length every call. */
  read(state: S): readonly number[];
  /** Indices of angle values in radians; they blend the short way round. */
  angles?: readonly number[];
  /** Indices that form the position, for the error distance. Default [0, 1]. */
  position?: readonly number[];
}

export interface PredictorOptions<S, I> {
  rules: GameRules<S, I>;
  /** The local player's slot, and how many slots the match has. */
  slot: number;
  players: number;
  view: LocalView<S>;
  /** A correction larger than this (world units) snaps instead of blending (P1-S01: 60 of 1000). */
  snapDistance: number;
  /** Time constant for blending small corrections away, in ms. Default 100. */
  blendMs?: number;
}

export interface PredictorStats {
  /** Local inputs the server hasn't acknowledged yet. */
  pending: number;
  reconciles: number;
  /** Corrections larger than snapDistance (visible jumps). */
  hardSnaps: number;
  /** Distance between the shown and the corrected position at each reconcile. */
  error: { p50: number; p95: number; max: number };
}

const TAU = 2 * Math.PI;
const wrap = (a: number) => a - TAU * Math.round(a / TAU);

/**
 * Client-side prediction for the local player (P1-S01 → P2-SIM-03), using the game's own rules:
 *
 * - `step(seq, input)` once per local input step (the host's FixedStepper, at the server tick rate):
 *   the input is remembered as pending and the predicted state advances one step.
 * - `reconcile(snapshotBytes, ack)` on every server snapshot: the authoritative state is decoded,
 *   inputs the server has applied (seq ≤ ack) are dropped, and the rest are replayed on top.
 *   The difference between what was shown and the corrected prediction is blended away over
 *   `blendMs`, or snapped if it is larger than `snapDistance`.
 * - `view(alpha, now)`: the local player's values to draw this frame, interpolated between the last
 *   two predicted steps (alpha = the stepper's lag / stepMs), plus any remaining correction.
 *
 * Other players are replayed with their idle input; draw them from the SnapshotTimeline instead.
 */
export class Predictor<S, I> {
  readonly #rules: GameRules<S, I>;
  readonly #slot: number;
  readonly #players: number;
  readonly #view: LocalView<S>;
  readonly #angles: ReadonlySet<number>;
  readonly #position: readonly number[];
  readonly #snapDistance: number;
  readonly #blendMs: number;
  readonly #rng = createRng(0);
  #state: S | null = null;
  #prev: number[] = [];
  #curr: number[] = [];
  #pending: { seq: number; input: I }[] = [];
  #correction: { offset: number[]; at: number } | null = null;
  /** Step fraction of the last frame drawn; corrections are measured at the same point. */
  #lastAlpha = 1;
  #reconciles = 0;
  #hardSnaps = 0;
  readonly #errors = new Ring(200);

  constructor(options: PredictorOptions<S, I>) {
    this.#rules = options.rules;
    this.#slot = options.slot;
    this.#players = options.players;
    this.#view = options.view;
    this.#angles = new Set(options.view.angles ?? []);
    this.#position = options.view.position ?? [0, 1];
    this.#snapDistance = options.snapDistance;
    this.#blendMs = options.blendMs ?? 100;
  }

  #inputs(input: I): I[] {
    return Array.from({ length: this.#players }, (_, s) =>
      s === this.#slot ? input : this.#rules.idleInput(),
    );
  }

  /** One local input step: remember the input (send it with this seq) and advance the prediction. */
  step(seq: number, input: I): void {
    this.#pending.push({ seq, input });
    if (this.#pending.length > 256) this.#pending.shift(); // server not acking: keep the newest
    if (this.#state === null || this.#rules.outcome(this.#state)) return;
    this.#state = this.#rules.step(this.#state, this.#inputs(input), this.#rng);
    this.#prev = this.#curr;
    this.#curr = [...this.#view.read(this.#state)];
  }

  /**
   * The server's state (the game's snapshot bytes; the predictor decodes its own copy) and the last
   * input seq from this client that it has applied.
   */
  reconcile(snapshot: Uint8Array, ack: number, now: number): void {
    this.#reconciles++;
    // Where the local player would be drawn now (old prediction + any correction still running).
    const before = this.#state === null ? null : this.#display(this.#lastAlpha, now);
    while (this.#pending.length > 0 && (this.#pending[0]?.seq ?? 0) <= ack) this.#pending.shift();

    let state = this.#rules.decodeSnapshot(snapshot, this.#rng);
    let curr = [...this.#view.read(state)];
    let prev = curr;
    for (const { input } of this.#pending) {
      if (this.#rules.outcome(state)) break;
      state = this.#rules.step(state, this.#inputs(input), this.#rng);
      prev = curr;
      curr = [...this.#view.read(state)];
    }
    this.#state = state;
    this.#prev = prev;
    this.#curr = curr;

    if (!before) return; // first snapshot: nothing on screen to correct
    const after = this.#interpolate(this.#lastAlpha);
    const offset = before.map((v, i) => this.#diff(v, after[i] ?? v, i));
    const error = Math.hypot(...this.#position.map((i) => offset[i] ?? 0));
    this.#errors.push(error);
    if (error > this.#snapDistance) {
      this.#hardSnaps++;
      this.#correction = null;
    } else {
      this.#correction = { offset, at: now };
    }
  }

  #diff(a: number, b: number, i: number): number {
    return this.#angles.has(i) ? wrap(a - b) : a - b;
  }

  /** Angles come out in [-π, π). */
  #norm(v: number, i: number): number {
    return this.#angles.has(i) ? wrap(v) : v;
  }

  #interpolate(alpha: number): number[] {
    const t = Math.min(1, Math.max(0, alpha));
    return this.#curr.map((c, i) => {
      const p = this.#prev[i] ?? c;
      return this.#norm(p + this.#diff(c, p, i) * t, i);
    });
  }

  /** Interpolated prediction plus the remaining correction, without side effects. */
  #display(alpha: number, now: number): number[] {
    const values = this.#interpolate(alpha);
    if (!this.#correction) return values;
    const weight = Math.exp(-(now - this.#correction.at) / this.#blendMs);
    if (weight < 0.01) return values;
    const { offset } = this.#correction;
    return values.map((v, i) => this.#norm(v + (offset[i] ?? 0) * weight, i));
  }

  /**
   * The local player's values to draw now. `alpha` is how far the host's input stepper is into the
   * current step (lagMs / stepMs). Null until the first snapshot.
   */
  view(alpha: number, now: number): readonly number[] | null {
    if (this.#state === null) return null;
    this.#lastAlpha = alpha;
    if (this.#correction && now - this.#correction.at > this.#blendMs * Math.log(100))
      this.#correction = null; // under 1% left
    return this.#display(alpha, now);
  }

  /** The full predicted state at the latest step (for anything else the game wants to predict). */
  get state(): S | null {
    return this.#state;
  }

  /** For telemetry (P2-AN-04 perf_sample). */
  get stats(): PredictorStats {
    const errors = this.#errors.values;
    return {
      pending: this.#pending.length,
      reconciles: this.#reconciles,
      hardSnaps: this.#hardSnaps,
      error: {
        p50: percentile(errors, 50),
        p95: percentile(errors, 95),
        max: errors.length > 0 ? Math.max(...errors) : Number.NaN,
      },
    };
  }

  /** Forget the prediction (new match or reconnect). Pending inputs are kept until acked. */
  reset(): void {
    this.#state = null;
    this.#prev = [];
    this.#curr = [];
    this.#correction = null;
  }
}
